import { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import PropTypes from 'prop-types';

import cm from '../../utils/class-merge';

const STRING_AVATAR_DEFAULT_PROPS = {
  fontSize: 14,
  height: 40,
  width: 40,
};

export function StringToColor(string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.substr(-2);
  }

  return color;
}

export const generateInitials = (name, placeHolder, initials) => {
  if (name) {
    const nameArray = name.trim().split(/\s/).filter((str) => str !== '');
    if (initials === 1) return nameArray[0][0];
    if (initials >= 2) return nameArray.length === 1 ? nameArray[0][0] : `${nameArray[0][0]}${nameArray[1][0]}`;
  }
  return placeHolder;
};

export const StringAvatar = (props) => {
  const { name, fontSize, fontWeight, height, width, initials = 2, className, placeHolder = 'SP', transparentWhiteAvatar = false, whiteAvatar = false, squareBorder = false, borderGray } = props;

  const classNames = cm(
    className,
    borderGray && 'border-gray',
    whiteAvatar && 'white-avatar',
    transparentWhiteAvatar && 'transparent-white',
    squareBorder && 'square-border',
  );

  const initialLetters = useMemo(() => generateInitials(name, placeHolder, initials), [initials, name]);

  const backgroundColor = useMemo(() => StringToColor(name ?? placeHolder), [name]);

  return (
    <Avatar className={classNames} sx={{ textTransform: 'uppercase', bgcolor: backgroundColor, fontSize, height, width, fontWeight }}>
      {initialLetters}
    </Avatar>
  );
};

StringAvatar.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
  fontSize: PropTypes.number,
  fontWeight: PropTypes.number,
  height: PropTypes.number,
  width: PropTypes.number,
  initials: PropTypes.oneOf([1, 2]),
  placeHolder: PropTypes.string,
  whiteAvatar: PropTypes.bool,
  transparentWhiteAvatar: PropTypes.bool,
  squareBorder: PropTypes.bool,
  borderGray: PropTypes.bool,
};

StringAvatar.defaultProps = STRING_AVATAR_DEFAULT_PROPS;

export const AvatarImage = (props) => {
  const { src, alt, name, fontSize, fontWeight, height, width, whiteAvatar = false, transparentWhiteAvatar = false, className, placeHolder = 'SP', squareBorder = false, initials = 2, borderGray } = props;

  if (!src) {
    return <StringAvatar initials={initials} className={className} name={name} fontWeight={fontWeight} fontSize={fontSize} height={height} width={width} placeHolder={placeHolder} borderGray={borderGray} squareBorder={squareBorder} whiteAvatar={whiteAvatar} />;
  }

  const classNames = cm(
    className,
    borderGray && 'border-gray',
    whiteAvatar && 'white-avatar',
    transparentWhiteAvatar && 'transparent-white',
    squareBorder && 'square-border',
  );

  return (
    <Avatar className={classNames} sx={{ height, width, fontSize, fontWeight }} alt={alt} src={src} />
  );
};

AvatarImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  name: PropTypes.string,
  fontSize: PropTypes.number,
  height: PropTypes.number,
  width: PropTypes.number,
  placeHolder: PropTypes.string,
  squareBorder: PropTypes.bool,
  whiteAvatar: PropTypes.bool,
  fontWeight: PropTypes.number,
  className: PropTypes.string,
  transparentWhiteAvatar: PropTypes.bool,
  initials: PropTypes.oneOf([1, 2]),
  borderGray: PropTypes.bool,
};

AvatarImage.defaultProps = STRING_AVATAR_DEFAULT_PROPS;
